import React from 'react';
import { observer } from 'mobx-react';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { StandardPage } from 'ui/base/page/standard_page/standard_page';
import { BackButton } from 'ui/base/button/back_button';

import { OrderedMap } from 'services/base/types';
import { BasePath } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import baseStyles from './manage_pangolins_list.module.css';
import Messages from './manage_pangolins_list_messages';

type ManagePangolinsListStyles = {
  row: string;
  backButtonContainer: string;
  titleText: string;
  column: string;
  containerRow: string;
  name: string;
  labelText: string;
  plusContainer: string;
  plusIcon: string;
  link: string;
  lastRow: string;
};

const themedStyles: ThemedStyles<ManagePangolinsListStyles> = {
  [Theme.DAYLIGHT]: {
    row: baseStyles.baseRow,
    backButtonContainer: baseStyles.baseBackButtonContainer,
    titleText: baseStyles.daylightTitleText,
    column: baseStyles.baseColumn,
    containerRow: baseStyles.daylightContainerRow,
    name: baseStyles.daylightName,
    labelText: baseStyles.daylightLabelText,
    plusContainer: baseStyles.daylightPlusContainer,
    plusIcon: baseStyles.daylightPlusIcon,
    link: baseStyles.daylightLink,
    lastRow: baseStyles.baseLastRow,
  },
  [Theme.MIDNIGHT]: {
    row: baseStyles.baseRow,
    backButtonContainer: baseStyles.baseBackButtonContainer,
    titleText: baseStyles.midnightTitleText,
    column: baseStyles.baseColumn,
    containerRow: baseStyles.midnightContainerRow,
    name: baseStyles.midnightName,
    labelText: baseStyles.midnightLabelText,
    plusContainer: baseStyles.midnightPlusContainer,
    plusIcon: baseStyles.midnightPlusIcon,
    link: baseStyles.midnightLink,
    lastRow: baseStyles.baseLastRow,
  },
};

type ManagePangolinsComponentProps = {
  pangolin: Pangolin;
};

const ManagePangolinsComponent = observer(({ pangolin }: ManagePangolinsComponentProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Link path={{ kind: BasePath.MANAGE_PANGOLIN_VIEW, pangolinID: pangolin.id }} className={styles.link}>
      <div className={styles.containerRow}>
        <div className={styles.name}>
          {pangolin.nickname}
        </div>
        <ShieldIcon colorCode={pangolin.colorCode} onlineStatus={pangolin.onlineStatus} size={ShieldSize.SMALL}/>
      </div>
    </Link>
  );
});

type BaseManagePangolinsListProps = {
  pangolins: OrderedMap<string, Pangolin>;
};

export const BaseManagePangolinsList = observer(({ pangolins }: BaseManagePangolinsListProps) => {
  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <div className={styles.row}>
        <div className={styles.backButtonContainer}>
          <BackButton/>
        </div>
        <div className={styles.titleText}>
          {Messages.title()}
        </div>
      </div>
      <div className={styles.column}>
        {pangolins.map((p) => (
          <ManagePangolinsComponent pangolin={p} key={p.id}/>
        ))}
        <Link path={{ kind: BasePath.ADD_PANGOLIN_SETUP }} className={styles.link}>
          <div className={styles.lastRow}>
            <div className={styles.labelText}>
              {Messages.addPangolin()}
            </div>
            <div className={styles.plusContainer}>
              <div className={styles.plusIcon}/>
            </div>
          </div>
        </Link>
      </div>
    </StandardPage>
  );
});

export const ManagePangolinsList = observer(() => {
  const accounts = useAccountsDomain();

  const user = accounts.user;
  if (!user) return <Redirect />;

  const pangolins = accounts.pangolins;

  return (
    <BaseManagePangolinsList
      pangolins={pangolins}
    />
  );
});
